import type { ItemView, LearnerClient } from "@tutor/live";
import { MAX_TEACH_BACK } from "@tutor/protocol";
import { useId, useState, type SubmitEvent } from "react";
import { useT } from "../i18n";

// « Explique avec tes mots » (ADR-0059): on a finished exercise the child got right, when
// the engine asks. The explanation is graded; a sure pass counts on the progression and
// anything else is only kept on the page, with no verdict shown: a weak explanation is
// not evidence against the child. « Suivant » stays, so it is never a dead end.

export function TeachBack(props: {
  item: ItemView;
  client?: Pick<LearnerClient, "explain">;
  /** A board page: the explanation as it was, no form. */
  readOnly?: boolean;
}) {
  const { item, client, readOnly = false } = props;
  const t = useT();
  const titleId = useId();
  const inputId = useId();
  const [draft, setDraft] = useState("");
  const teachBack = item.teachBack;
  if (teachBack === undefined) return null;
  if (readOnly && teachBack.state === "asked") return null;

  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = draft.trim();
    if (text === "" || client === undefined) return;
    client.explain(item.itemId, text);
    setDraft("");
  };

  return (
    <section className="teach-back" aria-labelledby={titleId} data-state={teachBack.state}>
      <h3 id={titleId} className="teach-back-title">
        {t("board.teachBack.title")}
      </h3>
      {teachBack.state === "asked" ? (
        client !== undefined && (
          <form className="teach-back-form" onSubmit={onSubmit}>
            <label htmlFor={inputId}>{t("board.teachBack.ask")}</label>
            <textarea
              id={inputId}
              className="teach-back-input"
              rows={2}
              maxLength={MAX_TEACH_BACK}
              placeholder={t("board.teachBack.placeholder")}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
            />
            <div className="teach-back-actions">
              <button type="submit" className="button" disabled={draft.trim() === ""}>
                {t("board.teachBack.send")}
              </button>
              <span className="teach-back-skip">{t("board.teachBack.skip")}</span>
            </div>
          </form>
        )
      ) : (
        <>
          <p className="teach-back-text">{teachBack.text}</p>
          <p className="teach-back-status" role="status">
            {t(
              teachBack.state === "sent"
                ? "board.teachBack.sent"
                : teachBack.counted
                  ? "board.teachBack.counted"
                  : "board.teachBack.kept",
            )}
          </p>
        </>
      )}
    </section>
  );
}
